// Bank data helpers (Financials v2, slice 1): pure shaping and formatting for
// the bank_status / bank_unmatched proxy reads and the bankUploadStatement
// result. No React state, no Firebase: unit-tested in bankData.test.js.
//
// Shapes: functions/src/types/bank.ts + the "Bank data (slice 1)" section of
// functions/src/handlers/financials/qboProxy.ts. Amounts are in the BANK's
// sign convention (money out negative; a card owed is negative) and `diff`
// is bank − books, 0 when they agree. Every figure these helpers print comes
// from a field of those responses; nothing is estimated or defaulted.
import { money } from "../views/common.jsx";

// ── The file ─────────────────────────────────────────────────────────────────

/** The callable's cap: 2 MB decoded (its base64 limit is 2.9M characters). */
export const MAX_FILE_BYTES = 2 * 1024 * 1024;

const TOO_BIG = "This file is larger than 2 MB. Export a shorter date range and upload that.";
// 32 KB per String.fromCharCode call: spreading 2 MB of bytes as arguments at
// once overflows the call stack.
const CHUNK = 0x8000;

/**
 * The file's bytes as base64, for bankUploadStatement's contentBase64.
 * Rejects with a plain message when the file is over MAX_FILE_BYTES; the
 * bytes are read with file.arrayBuffer() and never logged.
 */
export async function fileToBase64(file) {
  if (!file) throw new Error("No file was chosen.");
  if (file.size > MAX_FILE_BYTES) throw new Error(TOO_BIG);
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes.length > MAX_FILE_BYTES) throw new Error(TOO_BIG);
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// ── Accounts ─────────────────────────────────────────────────────────────────

const QBO_TYPE = { bank: "Bank", card: "Credit Card" };

/**
 * The QuickBooks accounts (get_accounts) a bank account or card can map to:
 * accountType "Bank" for kind "bank", "Credit Card" for kind "card", sorted
 * by name. An account without an id cannot be mapped and is left out.
 */
export const accountsForKind = (accounts, kind) =>
  (Array.isArray(accounts) ? accounts : [])
    .filter((a) => a && a.id && a.accountType === QBO_TYPE[kind])
    .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? ""), "en", { sensitivity: "base" }));

/**
 * The mapped accounts (bank_status) a CSV without an account number can be
 * stored against. A generic CSV's kind is only assumed "bank" by the parser
 * (the server checks the kind only for the Bank of America and QFX formats),
 * so every mapped account is offered for one; otherwise the file's kind.
 */
export const mappedForFile = (accounts, summary) =>
  (Array.isArray(accounts) ? accounts : [])
    .filter((a) => a && (summary?.format === "generic_csv" || a.kind === summary?.kind));

/** The label a newly mapped account starts with. */
export const defaultLabel = (kind, last4) => `${kind === "card" ? "Card" : "Bank"} ··${last4 ?? ""}`;

/**
 * "{label} ··{last4}", the way every screen names an account. A label that
 * already ends in the last four (defaultLabel's "Bank ··4821") is not
 * repeated.
 */
export const acctName = (label, last4) => {
  const l = String(label ?? "").trim();
  if (!last4) return l || "—";
  if (!l) return `··${last4}`;
  return l.endsWith(last4) ? l : `${l} ··${last4}`;
};

// ── Dates ────────────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** YYYY-MM-DD read by its parts: new Date("2026-09-22") is UTC midnight, the 21st every New York evening. */
const dayParts = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof iso === "string" ? iso : "");
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, mo, d };
};

/** "2026-09-22" → "Sep 22"; "—" when there is no date. */
export const fmtDay = (iso) => {
  const p = dayParts(iso);
  return p ? `${MONTHS[p.mo - 1]} ${p.d}` : "—";
};

/** "2026-09-22" → "Sep 22, 2026"; "—" when there is no date. */
export const fmtDayYear = (iso) => {
  const p = dayParts(iso);
  return p ? `${MONTHS[p.mo - 1]} ${p.d}, ${p.y}` : "—";
};

/**
 * Server prose (a comparison's note, a lastCompareError) with its YYYY-MM-DD
 * dates written the way the screens write dates: "Sep 1, 2026".
 */
export const withDays = (text) =>
  String(text ?? "").replace(/\b\d{4}-\d{2}-\d{2}\b/g, (m) => (dayParts(m) ? fmtDayYear(m) : m));

/** {start, end} → "Sep 1 – Sep 22" (one day: "Sep 22"; neither: "—"). */
export const fmtRange = (range) => {
  const s = dayParts(range?.start) ? range.start : null;
  const e = dayParts(range?.end) ? range.end : null;
  if (s && e) return s === e ? fmtDay(s) : `${fmtDay(s)} – ${fmtDay(e)}`;
  if (s || e) return fmtDay(s || e);
  return "—";
};

const isMs = (ms) => typeof ms === "number" && Number.isFinite(ms);

/** Epoch ms → the local calendar day, YYYY-MM-DD. */
export const localDay = (ms) => {
  if (!isMs(ms)) return null;
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Epoch ms → "Sep 22 · 9:14 AM", local time. Built by hand: toLocaleTimeString puts a narrow no-break space before AM on newer ICU. */
export const fmtWhen = (ms) => {
  if (!isMs(ms)) return "—";
  const d = new Date(ms);
  const h = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${MONTHS[d.getMonth()]} ${d.getDate()} · ${h % 12 || 12}:${mm} ${h < 12 ? "AM" : "PM"}`;
};

// ── Counts ───────────────────────────────────────────────────────────────────

const count = (n) => (typeof n === "number" && Number.isFinite(n) ? n : 0);
const numOrNull = (n) => (typeof n === "number" && Number.isFinite(n) ? n : null);
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Text cut to n characters, the last of them an ellipsis. */
export const truncate = (s, n) => {
  const t = String(s ?? "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

// ── Overview + Expenses: bank_status → rows ──────────────────────────────────

const toRow = (a) => {
  const c = a?.lastCompare ?? null;
  const error = a?.lastCompareError ? withDays(a.lastCompareError) : null;
  const notInBooks = c ? count(c.notInBooks) : null;
  const notAtBank = c ? count(c.notAtBank) : null;
  let sub;
  let subAttn = false;
  if (c) {
    if (notInBooks > 0) {
      sub = `${plural(notInBooks, "transaction", "transactions")} not in QuickBooks`;
      subAttn = true;
    } else if (notAtBank > 0) {
      sub = `${notAtBank} not cleared at the bank`;
    } else {
      sub = "matched";
    }
  } else if (error) {
    sub = error;
    subAttn = true;
  } else {
    // Mapped, never compared (no upload yet, or remapped since).
    sub = "not compared yet";
  }
  return {
    accountId: a?.accountId ?? null,
    label: a?.label ?? "",
    last4: a?.last4 ?? "",
    kind: a?.kind ?? null,
    bank: c ? numOrNull(c.bankBalance) : null,
    books: c ? numOrNull(c.booksBalance) : null,
    diff: c ? numOrNull(c.diff) : null,
    asOf: c?.asOf ?? null,
    balanceAsOf: c?.balanceAsOf ?? null,
    notInBooks,
    notAtBank,
    matched: c ? count(c.matched) : null,
    sub,
    /** The sub-line is the not-in-QuickBooks count or an error: shown in amber. */
    subAttn,
    note: c?.note ? withDays(c.note) : null,
    /** lastCompareError: why the latest comparison failed (an older lastCompare may still be shown). */
    error,
    hasCompare: Boolean(c),
  };
};

const datesOf = (rows) => [...new Set(rows.map((r) => r.asOf).filter(Boolean))].sort();

/**
 * bank_status → what the Overview's Bank vs QuickBooks panel, its attention
 * line and the Expenses bank-feed line show.
 *  rows: one per mapped account (see toRow).
 *  attention: null when nothing needs it; else the not-in-QuickBooks count
 *    over the compared accounts, the latest statement date among the accounts
 *    that need attention (datesDiffer when they are not all one date), the
 *    account with the largest non-zero |diff| (worst), and the accounts that
 *    need attention (accountIds, which "Details" expands).
 *  notInBooksTotal / latestAsOf / datesDiffer: over the compared accounts.
 *  hasData: at least one account has a comparison.
 *  hasRows: at least one account has a comparison or a comparison error (the
 *    panel shows its table then; an error is never hidden behind "No bank data").
 */
export function bankOverview(status) {
  const accounts = Array.isArray(status?.accounts) ? status.accounts : [];
  const rows = accounts.map(toRow);
  const compared = rows.filter((r) => r.hasCompare);
  const notInBooksTotal = compared.reduce((s, r) => s + r.notInBooks, 0);
  const dates = datesOf(compared);
  const latestAsOf = dates.length ? dates[dates.length - 1] : null;

  let worst = null;
  for (const r of compared) {
    if (r.diff !== null && r.diff !== 0 && (!worst || Math.abs(r.diff) > Math.abs(worst.diff))) worst = r;
  }
  const needing = compared.filter((r) => r.notInBooks > 0 || (r.diff !== null && r.diff !== 0));
  let attention = null;
  if (needing.length) {
    const nd = datesOf(needing);
    attention = {
      notInBooks: notInBooksTotal,
      asOf: nd.length ? nd[nd.length - 1] : null,
      datesDiffer: nd.length > 1,
      worst: worst ? { accountId: worst.accountId, label: worst.label, last4: worst.last4, diff: worst.diff } : null,
      accountIds: needing.map((r) => r.accountId),
    };
  }
  return {
    rows,
    attention,
    notInBooksTotal,
    latestAsOf,
    datesDiffer: dates.length > 1,
    hasData: compared.length > 0,
    hasRows: rows.some((r) => r.hasCompare || r.error),
  };
}

/** "statement of Sep 22", or "statements through Sep 22" when the accounts' statements end on different days. */
export const statementPhrase = (asOf, datesDiffer) =>
  (datesDiffer ? `statements through ${fmtDay(asOf)}` : `statement of ${fmtDay(asOf)}`);

/** The Overview's attention row, as text (the component renders the same words). */
export const attentionText = (attention) => {
  if (!attention) return "";
  const parts = [];
  if (attention.notInBooks > 0) {
    parts.push(`${plural(attention.notInBooks, "bank transaction", "bank transactions")} not in QuickBooks yet`);
    parts.push(`from the ${statementPhrase(attention.asOf, attention.datesDiffer)}`);
    if (attention.worst) {
      parts.push(`${acctName(attention.worst.label, attention.worst.last4)} differs by ${money(attention.worst.diff)}`);
    }
  } else if (attention.worst) {
    parts.push(`${acctName(attention.worst.label, attention.worst.last4)} differs from QuickBooks by ${money(attention.worst.diff)}`);
    parts.push(`from the ${statementPhrase(attention.asOf, attention.datesDiffer)}`);
  }
  return parts.join(" · ");
};

// ── Settings: bank_status → the Source line ──────────────────────────────────

/**
 * "Statement uploads · last one Sep 22 (Chase, Visa)": the newest upload's
 * last date, and the accounts uploaded on the same local calendar day as it
 * (the weekly batch), from the five recent uploads bank_status returns.
 */
export const sourceLine = (status) => {
  const uploads = Array.isArray(status?.recentUploads) ? status.recentUploads : [];
  if (!uploads.length) return "No statement uploaded yet";
  const newest = uploads[0];
  const day = localDay(newest.uploadedAt);
  const labelOf = new Map((status?.accounts ?? []).map((a) => [a.accountId, a.label || a.accountId]));
  const ids = [];
  for (const u of uploads) {
    if (localDay(u.uploadedAt) === day && !ids.includes(u.accountId)) ids.push(u.accountId);
  }
  const names = ids.map((id) => labelOf.get(id) ?? id).join(", ");
  const when = dayParts(newest.range?.end) ? `last one ${fmtDay(newest.range.end)}` : `last one uploaded ${fmtDay(day)}`;
  return `Statement uploads · ${when}${names ? ` (${names})` : ""}`;
};

// ── Settings: the upload result ──────────────────────────────────────────────

const FORMAT_NAMES = {
  ofx: "QFX / OFX",
  bofa_csv: "Bank of America CSV",
  bofa_card_csv: "Bank of America card CSV",
  generic_csv: "CSV",
};
export const formatName = (format) => FORMAT_NAMES[format] ?? String(format ?? "—");
export const kindName = (kind) => (kind === "card" ? "credit card" : "bank account");

/** A {amount, asOf} balance → "$69,812.03 as of Sep 22, 2026". */
export const balanceText = (b) => {
  if (!b || typeof b.amount !== "number") return null;
  return b.asOf ? `${money(b.amount)} as of ${fmtDayYear(b.asOf)}` : `${money(b.amount)} (the file gives no date)`;
};

/** A comparison summary → "matched 41 · 5 not in QuickBooks · 0 not cleared at the bank · diff -$1,192.00". */
export const compareLine = (c) => [
  `matched ${count(c?.matched)}`,
  `${count(c?.notInBooks)} not in QuickBooks`,
  `${count(c?.notAtBank)} not cleared at the bank`,
  numOrNull(c?.diff) === null ? "balance not compared" : `diff ${money(c.diff)}`,
].join(" · ");

/**
 * An ok bankUploadStatement result → the lines its result block shows:
 * the stored and new counts (the changed count arrives as a warning, so the
 * warnings pass through as written), the balance with its date, and the
 * comparison line or the comparison error, with the comparison's own note.
 */
export function describeUpload(result) {
  const txns = count(result?.txnCount);
  const c = result?.compare ?? null;
  return {
    stored: `${plural(txns, "transaction", "transactions")} stored · ${count(result?.newCount)} new`,
    balance: balanceText(result?.balance) ?? "None in the file",
    compare: c ? compareLine(c) : null,
    compareError: result?.compareError ? withDays(result.compareError) : null,
    note: c?.note ? withDays(c.note) : null,
    warnings: Array.isArray(result?.warnings) ? result.warnings.filter(Boolean) : [],
  };
}
